import { useState } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { Plus, Search, Mail, Truck, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", service_type: "", contact_name: "", email: "", phone: "", conditions: "", notes: "" };

function SupplierDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...form }; delete payload.id; delete payload.created_at;
      if (editing) await api.patch(`/suppliers/${initial.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(editing ? "Proveedor actualizado" : "Proveedor creado");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editing ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5"><Label>Nombre *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="supplier-name-input" /></div>
          <div className="space-y-1.5"><Label>Tipo de servicio</Label><Input value={form.service_type} onChange={(e) => set("service_type", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Persona de contacto</Label><Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Condiciones</Label><Input value={form.conditions} onChange={(e) => set("conditions", e.target.value)} placeholder="Ej: 30 días, 50% anticipo…" /></div>
          <div className="space-y-1.5"><Label>Notas</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="resize-none" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="supplier-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Proveedores() {
  const { can } = useAuth();
  const [search, setSearch] = useState("");
  const { data: suppliers, loading, refetch } = useFetch("/suppliers", search ? { search } : {});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este proveedor?")) return;
    try { await api.delete(`/suppliers/${id}`); toast.success("Proveedor eliminado"); refetch(); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title="Proveedores" subtitle="Colaboradores y servicios externos de la agencia">
        {can("crm", "create") && <Button size="sm" className="gap-1.5" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-supplier-btn"><Plus className="h-4 w-4" /> Nuevo proveedor</Button>}
      </PageHeader>

      <div className="relative w-full sm:max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar proveedor…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="supplier-search" />
      </div>

      {loading ? <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        : (suppliers || []).length === 0 ? <Card className="border-border shadow-none py-16 text-center text-sm text-muted-foreground">No hay proveedores.</Card>
        : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((s) => (
            <Card key={s.id} className="border-border shadow-none p-5 hover:shadow-sm transition-shadow duration-200" data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-gold/15 text-gold flex items-center justify-center"><Truck className="h-5 w-5" /></div>
                <div className="flex items-center gap-2">
                  {s.conditions && <Badge variant="secondary" className="font-normal text-xs">{s.conditions}</Badge>}
                  {(can("crm", "edit") || can("crm", "delete")) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`supplier-menu-${s.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {can("crm", "edit") && <DropdownMenuItem onClick={() => { setEditing(s); setDialogOpen(true); }}><Pencil className="h-4 w-4 mr-2" /> Editar</DropdownMenuItem>}
                        {can("crm", "delete") && <DropdownMenuItem onClick={() => del(s.id)} className="text-destructive focus:text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Eliminar</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              <h3 className="font-heading font-bold text-lg mt-4">{s.name}</h3>
              <p className="text-sm text-muted-foreground">{s.service_type || "—"}</p>
              <div className="mt-4 pt-4 border-t border-border space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground"><span className="text-xs w-16">Contacto</span><span className="text-foreground font-medium">{s.contact_name || "—"}</span></div>
                {s.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-3.5 w-3.5" /><span className="text-primary truncate">{s.email}</span></div>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {dialogOpen && <SupplierDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={refetch} />}
    </div>
  );
}
